import { applyExclusiveOptions, choiceValues, parsePageValidation } from "@patient-watch/forms/pageValidation";
import { useEffect, useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";

/**
 * Keeps a multiple-choice question's exclusive choice ("None of the above", "Prefer not to say") on its own:
 * picking it clears the other choices, and picking any other clears it. Driven by the page's `exclusive_option`
 * rules in pages.page_validation; does nothing on pages without them. The multiple-choice counterpart of
 * useExclusiveCheckboxes.
 */
export function useExclusiveOptions(form: UseFormReturn<Record<string, unknown>>, rawPageValidation: unknown) {
    const validation = useMemo(() => {
        const parsed = parsePageValidation(rawPageValidation);
        return parsed?.rules.some((rule) => rule.type === "exclusive_option") ? parsed : null;
    }, [rawPageValidation]);

    useEffect(() => {
        if (!validation) return;
        const inputIds = new Set(
            validation.rules.flatMap((rule) => (rule.type === "exclusive_option" ? [rule.input_id] : []))
        );
        // The answer before each change, so the choice just picked is the one that stays.
        const previous = new Map([...inputIds].map((id) => [id, choiceValues(form.getValues(id))]));
        const subscription = form.watch((values, { name, type }) => {
            if (!name || !inputIds.has(name)) return;
            const next = choiceValues(values[name]);
            // Only the patient's own picks are adjusted; the setValue below arrives without a type.
            const adjusted =
                type === "change" ? applyExclusiveOptions(validation, name, previous.get(name) ?? [], next) : next;
            previous.set(name, adjusted);
            if (adjusted.length !== next.length || adjusted.some((value, index) => value !== next[index])) {
                form.setValue(name, adjusted, { shouldDirty: true });
            }
        });
        return () => subscription.unsubscribe();
    }, [form, validation]);
}
